<template>
    <v-responsive :aspect-ratio="aspectRatio" @click="onPlayerClick" class="video-responsive">
        <v-container fluid pa-0 class="jwplayer-layer">
            <v-img :src="previewImage" contain v-if="previewImage" />

            <div v-else :style="videoContainerStyle">
                <div :id="'jwplayer-' + getUniqueId()"></div>
            </div>

            <div class="player-control">
                <span class="d-flex fill-height align-center text-xs-center white--text">
                    <i v-if="videoPlayed" class="fas fa-play" :class="{'animation-play': videoPlayed}"></i>
                    <i v-if="!videoPlayed" class="fas fa-pause" :class="{'animation-pause': !videoPlayed}"></i>
                </span>
            </div>

            <div class="play-icon" v-if="!videoPlayed">
                <div class="d-flex fill-height align-center justify-center">
                    <v-btn icon large color="primary" fab>
                        <v-icon size="40">play_arrow</v-icon>
                    </v-btn>
                </div>
            </div>
        </v-container>

        <v-dialog v-if="playInModal" v-model="displayDialog" height="90vh" :width="'calc(90vh*' + aspectRatio + ')'" content-class="video-container">
            <div class="modal-player">
                <v-btn icon class="close-icon white--text" @click="displayDialog = false">
                    <v-icon>close</v-icon>
                </v-btn>

                <div :id="'jwplayer-' + getUniqueId() + '-dialog'"></div>

                <div class="player-control" @click="modalPlayerClick" v-if="!controlsInModal">
                    <span class="d-flex fill-height align-center text-xs-center white--text">
                        <i v-if="modalVideoPlayed" class="fas fa-play" :class="{'animation-play': modalVideoPlayed}"></i>
                        <i v-if="!modalVideoPlayed" class="fas fa-pause" :class="{'animation-pause': !modalVideoPlayed}"></i>
                    </span>
                </div>
            </div>
        </v-dialog>
    </v-responsive>
</template>

<script>
import SearchResults from '@breakmedia/prime-vue-search/src/SearchResults';
import {mapGetters} from 'vuex';

export default {
    name: 'JwPlayer',
    props: {
        autoplay: Boolean,
        loop: Boolean,
        controls: Boolean,
        controlsInModal: Boolean,
        apiKey: String,
        videoLink: String,
        aspectRatio: Number,
        playInModal: Boolean,
        mute: Boolean,
        muteInModal: Boolean,
        previewImage: String,
        videoPlayerKey: String,
        fullHeight: Boolean,
    },
    data() {
        return {
            maxTries: 4,
            tries: 0,
            interval: null,
            displayDialog: false,
            modalVideoPlayed: true,
            videoPlayed: this.autoplay,
            videoContainerStyle: {
                'max-width': 'unset',
                width: '100%',
            },
        };
    },
    created() {
        if (this.apiKeyComputed && !document.getElementById('jwplayerScript')) {
            let script = document.createElement('script');
            script.src = `https://cdn.jwplayer.com/libraries/${this.apiKeyComputed}.js?hash=${this.getRandomString()}`;
            script.id = 'jwplayerScript';
            script.setAttribute('type', 'text/javascript');
            document.getElementsByTagName('script')[0].parentNode.insertBefore(script, document.getElementsByTagName('script')[0]);
        }
    },
    mounted() {
        let playerInitialized = false;
        this.interval = setInterval(() => {
            if (window.jwplayer) {
                if (!this.previewImage) {
                    this.setupPlayer('jwplayer-' + this.getUniqueId(), this.playerSettings);
                    playerInitialized = true;
                }
            }
            if (playerInitialized || this.tries >= this.maxTries) {
                clearInterval(this.interval);
            }

            this.tries++;
        }, 500);

        if (this.fullHeight) {
            this.onResize();
            window.addEventListener('resize', this.onResize);
        }
    },
    beforeDestroy() {
        if (this.interval) {
            clearInterval(this.interval);
        }

        if (this.fullHeight) {
            window.removeEventListener('resize', this.onResize);
        }
    },
    computed: {
        playerSettings() {
            return {
                file: this.videoLink,
                autostart: this.autoplay,
                repeat: this.loop,
                controls: this.controls,
                mute: this.mute,
            };
        },
        ...mapGetters(SearchResults.name, [
            'searchConfig',
        ]),
        apiKeyComputed() {
            return this.apiKey || this.searchConfig('videoPlayerApiKey');
        },
    },
    methods: {
        setupPlayer(id, settings, dropInstance = false) {
            if (dropInstance) {
                window.jwplayer(id).remove();
            }
            this.$nextTick(() => {
                window.jwplayer(id).setup(settings);
                window.jwplayer(id).on('ready', () => {
                    if (this.autoplay) {
                        this.play = true;
                    }
                });
                window.jwplayer(id).on('error', () => {
                    this.setupPlayer(true);
                });
            });
        },
        getRandomString() {
            return (
                new Date().getTime() +
                Math.random()
                    .toString(36)
                    .substring(7)
            );
        },
        onPlayerClick() {
            if (window.jwplayer) {
                if (this.playInModal) {
                    this.setupPlayer('jwplayer-' + this.getUniqueId() + '-dialog', {
                        ...this.playerSettings,
                        controls: this.controlsInModal,
                        mute: this.muteInModal,
                    });
                    this.displayDialog = true;
                } else {
                    this.videoPlayed = !this.videoPlayed;
                }
            }
        },
        modalPlayerClick() {
            this.modalVideoPlayed = !this.modalVideoPlayed;
        },
        getUniqueId() {
            return this.videoPlayerKey ? this.videoPlayerKey : this._uid;
        },
        onResize() {
            if (this.$vuetify.breakpoint.smAndUp) {
                this.videoContainerStyle.width = this.$el.parentElement.offsetHeight * this.aspectRatio + 'px';
            } else {
                this.videoContainerStyle.width = '100%';
            }
        },
    },
    watch: {
        displayDialog(val) {
            if (val && !this.previewImage) {
                window.jwplayer('jwplayer-' + this.getUniqueId()).pause();
            } else {
                if (!this.previewImage) {
                    window.jwplayer('jwplayer-' + this.getUniqueId()).stop();
                    if (this.autoplay) {
                        window.jwplayer('jwplayer-' + this.getUniqueId()).play();
                    }
                }
                window.jwplayer('jwplayer-' + this.getUniqueId() + '-dialog').stop();
            }
        },
        modalVideoPlayed(val) {
            val ?
                window.jwplayer('jwplayer-' + this.getUniqueId() + '-dialog').play() :
                window.jwplayer('jwplayer-' + this.getUniqueId() + '-dialog').pause();
        },
        videoPlayed(val) {
            if (!this.previewImage) {
                val ?
                    window.jwplayer('jwplayer-' + this.getUniqueId()).play() :
                    window.jwplayer('jwplayer-' + this.getUniqueId()).pause();
            }
        },
    },
};
</script>

<style scoped>
.jwplayer-layer,
.modal-player {
    position: relative;
}

.play-icon {
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
}

.play-icon .v-btn {
    flex-grow: 0 !important;
    font-size: 35px;
}

.player-control {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
}

.close-icon {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
}

@-webkit-keyframes animation-play {
    0% {
        opacity: 0;
        font-size: 25px;
    }
    50% {
        opacity: .7;
    }
    100% {
        opacity: 0;
        font-size: 40px;
    }
}

@keyframes animation-play {
    0% {
        opacity: 0;
        font-size: 25px;
    }
    50% {
        opacity: .7;
    }
    100% {
        opacity: 0;
        font-size: 40px;
    }
}

.animation-play {
    -webkit-animation-duration: 1s;
    animation-duration: 1s;
    -webkit-animation-delay: 0s;
    animation-delay: 0s;
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
    -webkit-animation-name: animation-play;
    animation-name: animation-play;
}

@-webkit-keyframes animation-pause {
    0% {
        opacity: 0;
        font-size: 25px;
    }
    50% {
        opacity: .7;
    }
    100% {
        opacity: 0;
        font-size: 40px;
    }
}

@keyframes animation-pause {
    0% {
        opacity: 0;
        font-size: 25px;
    }
    50% {
        opacity: .7;
    }
    100% {
        opacity: 0;
        font-size: 40px;
    }
}

.animation-pause {
    -webkit-animation-duration: 1s;
    animation-duration: 1s;
    -webkit-animation-delay: 0s;
    animation-delay: 0s;
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
    -webkit-animation-name: animation-pause;
    animation-name: animation-pause;
}
</style>

<style>
.video-container {
    margin: 0 !important;
}

.video-responsive .v-responsive__content {
    max-width: 100%;
}
</style>